<template>
  <div class="contractor-hall-page">
    <div class="page-header">
      <h2>{{ $t('driver.ContractorHall.line4_text') }}</h2>
      <el-tag type="success" effect="dark">
        可接众包单：{{ acceptContractorOrders ? '已开启' : '已关闭' }}
      </el-tag>
    </div>

    <!-- 开关控制 -->
    <div class="control-section">
      <el-card shadow="hover">
        <div class="control-content">
          <div class="control-info">
            <h3>{{ $t('driver.ContractorHall.line15_text') }}</h3>
            <p>{{ $t('driver.ContractorHall.line16_text') }}</p>
          </div>
          <el-switch
            v-model="acceptContractorOrders"
            @change="toggleAcceptContractorOrders"
            size="large"
            active-text="开"
            inactive-text="关"
          />
        </div>
      </el-card>
    </div>

    <!-- 筛选栏 -->
    <div class="filter-section">
      <el-row :gutter="16">
        <el-col :span="8">
          <el-select v-model="filterDistance" :placeholder="$t('driver.ContractorHall.line33_placeholder')" @change="loadAvailableTasks">
            <el-option label="距离最近" value="nearest" />
            <el-option label="配送费最高" value="highest_fee" />
            <el-option label="发布时间最新" value="latest" />
          </el-select>
        </el-col>
        <el-col :span="8">
          <el-select v-model="filterFeeRange" :placeholder="$t('driver.ContractorHall.line40_placeholder')" @change="loadAvailableTasks">
            <el-option label="全部" value="all" />
            <el-option label="10-20 元" value="10-20" />
            <el-option label="20-30 元" value="20-30" />
            <el-option label="30 元以上" value="30+" />
          </el-select>
        </el-col>
        <el-col :span="8">
          <el-button type="primary" icon="el-icon-refresh" @click="loadAvailableTasks" :loading="loading">
            刷新
          </el-button>
        </el-col>
      </el-row>
    </div>

    <!-- 任务列表 -->
    <div class="task-list" v-loading="loading">
      <div v-if="availableTasks.length === 0" class="empty-state">
        <div class="empty-icon">📭</div>
        <p>{{ $t('driver.ContractorHall.line59_text') }}</p>
        <p class="empty-tip">{{ $t('driver.ContractorHall.line60_text') }}</p>
      </div>

      <div v-else>
        <div v-for="task in availableTasks" :key="task.id" class="task-card">
          <div class="task-header">
            <div class="task-no">任务编号：{{ task.taskNo }}</div>
            <div class="task-fee">💰 ¥{{ task.fee || 0 }}</div>
          </div>

          <div class="task-route">
            <div class="route-item">
              <div class="route-dot pickup"></div>
              <div class="route-info">
                <div class="route-label">{{ $t('driver.ContractorHall.line74_text') }}</div>
                <div class="route-address">{{ task.pickupAddress || '未知地址' }}</div>
                <div class="route-contact" v-if="task.pickupContact">
                  📞 {{ task.pickupContact }} {{ task.pickupPhone }}
                </div>
              </div>
            </div>
            <div class="route-line"></div>
            <div class="route-item">
              <div class="route-dot delivery"></div>
              <div class="route-info">
                <div class="route-label">{{ $t('driver.ContractorHall.line85_text') }}</div>
                <div class="route-address">{{ task.deliveryAddress || '未知地址' }}</div>
                <div class="route-contact" v-if="task.deliveryContact">
                  📞 {{ task.deliveryContact }} {{ task.deliveryPhone }}
                </div>
              </div>
            </div>
          </div>

          <div class="task-footer" v-if="task.remark">
            <div class="remark-label">{{ $t('driver.ContractorHall.line95_text') }}</div>
            <div class="remark-text">{{ task.remark }}</div>
          </div>

          <div class="task-meta">
            <span>📅 {{ formatDate(task.createdAt) }}</span>
            <span v-if="task.fee">💵 配送费：¥{{ task.fee }}</span>
          </div>

          <div class="task-actions">
            <el-button type="primary" size="medium" @click="grabTask(task)" :loading="task.grabbing">
              🚀 立即抢单
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  getAvailableContractorTasks, 
  grabTask, 
  toggleAcceptContractorOrders,
  getDriver
} from '@/api/delivery'

export default {
  name: 'ContractorHallPage',
  data() {
    return {
      loading: false,
      acceptContractorOrders: false,
      filterDistance: 'nearest',
      filterFeeRange: 'all',
      availableTasks: [],
      currentPage: 0,
      pageSize: 20,
      total: 0,
      driverId: null
    }
  },
  created() {
    this.initDriverId()
    this.loadAvailableTasks()
  },
  methods: {
    /**
     * 初始化配送员 ID
     */
    async initDriverId() {
      try {
        // 从 localStorage 获取当前用户信息
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
        const userId = userInfo.id || userInfo.userId
        
        if (userId) {
          // 这里需要根据实际情况获取配送员 ID
          // 暂时从 userInfo 中获取 courierId
          this.driverId = userInfo.courierId || userId
        }
      } catch (error) {
        console.error('获取配送员 ID 失败:', error)
      }
    },

    /**
     * 加载可抢任务列表
     */
    async loadAvailableTasks() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          size: this.pageSize
        }
        
        const res = await getAvailableContractorTasks(params)
        this.availableTasks = res.data?.content || res.content || []
        this.total = res.data?.total || res.total || 0
        
        // 根据筛选条件排序
        this.sortTasks()
      } catch (error) {
        this.$message.error('加载任务列表失败')
      } finally {
        this.loading = false
      }
    },

    /**
     * 排序任务
     */
    sortTasks() {
      switch (this.filterDistance) {
        case 'nearest':
          // 这里可以集成地图 API 计算距离
          break
        case 'highest_fee':
          this.availableTasks.sort((a, b) => (b.fee || 0) - (a.fee || 0))
          break
        case 'latest':
          this.availableTasks.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          break
      }
    },

    /**
     * 抢单
     */
    async grabTask(task) {
      if (!this.driverId) {
        this.$message.warning('请先登录')
        return
      }

      if (!this.acceptContractorOrders) {
        this.$confirm('您还未开启接众包单，是否先开启？', '提示', {
          confirmButtonText: '开启',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.toggleAcceptContractorOrders(true)
        })
        return
      }

      try {
        this.$set(task, 'grabbing', true)
        
        await grabTask(task.id, this.driverId)
        
        this.$message.success('抢单成功！')
        
        // 从列表中移除
        this.availableTasks = this.availableTasks.filter(t => t.id !== task.id)
        
        // 跳转到任务详情页或任务列表
        this.$confirm('抢单成功！是否立即查看任务？', '提示', {
          confirmButtonText: '查看任务',
          cancelButtonText: '继续抢单',
          type: 'success'
        }).then(() => {
          this.$router.push('/delivery/grab-hall')
        }).catch(() => {
          // 继续抢单，刷新列表
          this.loadAvailableTasks()
        })
      } catch (error) {
        this.$message.error('抢单失败：' + (error.response?.data?.message || error.message))
      } finally {
        this.$set(task, 'grabbing', false)
      }
    },

    /**
     * 切换接众包单开关
     */
    async toggleAcceptContractorOrders(value) {
      try {
        // 这里需要配送员 ID，实际使用时需要从用户信息中获取
        const driverId = this.driverId
        
        if (!driverId) {
          this.$message.warning('请先完善配送员信息')
          this.acceptContractorOrders = !value
          return
        }

        await toggleAcceptContractorOrders(driverId, value)
        this.$message.success(value ? '已开启接众包单' : '已关闭接众包单')
      } catch (error) {
        this.acceptContractorOrders = !value
        this.$message.error('操作失败')
      }
    },

    /**
     * 格式化日期
     */
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diff = now - date
      
      // 1 分钟内
      if (diff < 60000) return '刚刚'
      // 1 小时内
      if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`
      // 24 小时内
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`
      // 超过 24 小时
      return date.toLocaleDateString('zh-CN')
    }
  }
}
</script>

<style lang="scss" scoped>
.contractor-hall-page {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h2 {
    margin: 0;
    font-size: 24px;
    color: #2c3e50;
  }
}

.control-section {
  margin-bottom: 20px;
  
  .control-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .control-info {
      h3 {
        margin: 0 0 8px 0;
        font-size: 16px;
        color: #2c3e50;
      }
      
      p {
        margin: 0;
        font-size: 13px;
        color: #999;
      }
    }
  }
}

.filter-section {
  margin-bottom: 20px;
}

.task-list {
  .task-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 16px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    transition: all 0.3s;
    
    &:hover {
      box-shadow: 0 4px 16px rgba(0,0,0,0.1);
      transform: translateY(-2px);
    }
    
    .task-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      
      .task-no {
        font-size: 14px;
        color: #999;
      }
      
      .task-fee {
        font-size: 20px;
        font-weight: bold;
        color: #4caf50;
      }
    }
    
    .task-route {
      margin-bottom: 16px;
      
      .route-item {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        
        .route-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          flex-shrink: 0;
          margin-top: 4px;
          
          &.pickup {
            background: #4caf50;
          }
          
          &.delivery {
            background: #2196f3;
          }
        }
        
        .route-info {
          flex: 1;
          
          .route-label {
            font-size: 12px;
            color: #999;
            margin-bottom: 4px;
          }
          
          .route-address {
            font-size: 15px;
            color: #2c3e50;
            margin-bottom: 6px;
          }
          
          .route-contact {
            font-size: 13px;
            color: #666;
          }
        }
      }
      
      .route-line {
        width: 2px;
        height: 20px;
        background: linear-gradient(to bottom, #4caf50, #2196f3);
        margin: 8px 0 8px 26px;
      }
    }
    
    .task-footer {
      display: flex;
      gap: 8px;
      margin-bottom: 12px;
      padding: 12px;
      background: #f5f7fa;
      border-radius: 6px;
      
      .remark-label {
        font-size: 13px;
        color: #999;
        flex-shrink: 0;
      }
      
      .remark-text {
        font-size: 13px;
        color: #666;
        flex: 1;
      }
    }
    
    .task-meta {
      display: flex;
      justify-content: space-between;
      font-size: 12px;
      color: #999;
      margin-bottom: 16px;
      padding-top: 12px;
      border-top: 1px solid #f0f0f0;
    }
    
    .task-actions {
      text-align: right;
    }
  }
  
  .empty-state {
    text-align: center;
    padding: 80px 20px;
    color: #999;
    
    .empty-icon {
      font-size: 80px;
      margin-bottom: 16px;
    }
    
    p {
      margin: 8px 0;
      font-size: 16px;
    }
    
    .empty-tip {
      font-size: 14px;
      color: #bbb;
    }
  }
}
</style>
